import { isDefined } from '@sama/js-core';
import { isVideoOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import {
  getProjectionType,
  hasCalibrationData,
  ProjectionType,
} from '../../../cross-team-shared/misc-utils/projection-utils';
import { LutData } from '../../../team-apis/core/lut-data';
import { CalibrationData } from '../../../team-apis/otterdam/calibration-data';
import { Project } from '../../../team-apis/otterdam/project';
import { calibrationInputKey, Task } from '../../../team-apis/otterdam/task';

export function getProjectionTypeFromTask(
  args: { task: Task; project: Project },
  outputName: string,
): ProjectionType {
  if (!hasCalibrationData(args.task, outputName)) {
    return 'pinhole';
  }

  const calibrationData = getCalibrationData(args.task, outputName);
  return getProjectionType(calibrationData, args.task, outputName);
}

export function isProjectionEnabled(
  args: { task: Task; project: Project },
  outputName: string,
  lutData: LutData | undefined,
) {
  return (
    isDefined(lutData?.angleToPixel) ||
    getProjectionTypeFromTask(args, outputName) !== 'lut'
  );
}

export function isUnprojectionEnabled(
  args: { task: Task; project: Project },
  outputName: string,
  lutData: LutData | undefined,
) {
  const immediatelyAvailableUnprojections: ProjectionType[] = [
    'brown_conrady',
    'kannala_brandt',
    'pinhole',
  ];
  return (
    immediatelyAvailableUnprojections.includes(
      getProjectionTypeFromTask(args, outputName),
    ) || isDefined(lutData?.pixelToLightray)
  );
}

export function isAtMostOneLutAvailable(args: {
  task: Task;
  project: Project;
}) {
  const unprojectionsWithLut: ProjectionType[] = [
    'deformed_cylindrical',
    'cylinder',
    'lut',
  ];

  const lutProjectionTypes = args.project.outputs
    .filter((output) => isVideoOutput(output))
    .map((output) => getProjectionTypeFromTask(args, output.name))
    .filter((projectionType) => unprojectionsWithLut.includes(projectionType));

  return lutProjectionTypes.length <= 1;
}

function getCalibrationData(task: Task, outputName: string): CalibrationData {
  return JSON.parse(task.data[`${outputName} ${calibrationInputKey}`]);
}
